import ReactDOM from "react-dom/client";
import * as htmlToImage from "html-to-image";
import { useEffect, useState } from "react";
export interface PosterSnapshotRendererProps {
  mapImageUrl?: string; // base64 or URL to the static map image
  overlayImage?: string; // optional base64 image of the canvas overlay
  posterSize: {
    width: number;
    height: number;
  };
  mapSize: {
    width: number;
    height: number;
  };
  borderColor: string;
  borderMargin: number;
  scale: number;
}

export async function renderSnapshotToImage(
  props: PosterSnapshotRendererProps
): Promise<string> {
  const containerId = "snapshot-container";
  let container = document.getElementById(containerId);

  if (!container) {
    container = document.createElement("div");
    container.id = containerId;
    document.body.appendChild(container);
  }
  container.style.all = "initial";
  container.style.pointerEvents = "none";
  container.style.position = "absolute"; // not fixed
  container.style.top = "0";
  container.style.left = "0";
  container.style.width = `${props.posterSize.width}px`;
  container.style.height = `${props.posterSize.height}px`;
  container.style.pointerEvents = "none";
  container.style.opacity = "1";

  const root = ReactDOM.createRoot(container);

  await new Promise<void>((resolve) => {
    root.render(<PosterSnapshotRenderer {...props} onLoadComplete={resolve} />);
  });
  await new Promise((resolve) => setTimeout(resolve, 100));

  const dataUrl = await htmlToImage.toPng(container, {
    skipFonts: true,
    cacheBust: false,
  });
  root.unmount();
  container.remove();

  return dataUrl;
}

const PosterSnapshotRenderer = ({
  mapImageUrl,
  overlayImage,
  posterSize,
  mapSize,
  borderColor,
  borderMargin,
  scale,
  onLoadComplete, // NEW
}: PosterSnapshotRendererProps & { onLoadComplete?: () => void }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [overlayLoaded, setOverlayLoaded] = useState(!overlayImage); // If no overlay, consider it "loaded"

  useEffect(() => {
    if (mapLoaded && overlayLoaded && onLoadComplete) {
      onLoadComplete();
    }
  }, [mapLoaded, overlayLoaded]);

  return (
    <div
      style={{
        position: "relative",
        width: `${posterSize.width}px`,
        height: `${posterSize.height}px`,
        backgroundColor: borderColor,
        padding: borderMargin,
        boxSizing: "border-box",
      }}
    >
      <img
        src={mapImageUrl}
        alt="Static map"
        style={{
          width: `${mapSize.width}px`,
          height: `${mapSize.height}px`,
          display: "block",
        }}
        onLoad={() => setMapLoaded(true)}
      />
      {overlayImage && (
        <img
          src={overlayImage}
          alt="Overlay"
          style={{
            position: "absolute",
            top: borderMargin,
            left: borderMargin,
            width: `${mapSize.width}px`,
            height: `${mapSize.height}px`,
            pointerEvents: "none",
          }}
          onLoad={() => setOverlayLoaded(true)}
        />
      )}
    </div>
  );
};

export async function imageUrlToBase64(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject("Failed to convert blob to base64 string");
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
